import type { z } from 'zod'
import {
	Controller,
	type FieldPath,
	useFieldArray,
	useForm,
	useFormContext,
} from 'react-hook-form'
import { Button } from '@cocoonspace/ui/components/button'
import { Checkbox } from '@cocoonspace/ui/components/checkbox'
import { TimePicker } from '@cocoonspace/ui/components/cocoon/time-picker'
import { Form } from '@cocoonspace/ui/components/form'
import { Label } from '@cocoonspace/ui/components/label'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { PlusIcon, TrashIcon } from 'lucide-react'

import { DAYS_OF_WEEK, type DayKey } from '../availabilities/constants'
import { workingHoursSchema } from '../availabilities/schemas'

interface TimeSlotWorkingHoursOutput {
	startTime: string
	endTime: string
}

interface WorkingHoursOutput {
	mon: TimeSlotWorkingHoursOutput[]
	tue: TimeSlotWorkingHoursOutput[]
	wed: TimeSlotWorkingHoursOutput[]
	thu: TimeSlotWorkingHoursOutput[]
	fri: TimeSlotWorkingHoursOutput[]
	sat: TimeSlotWorkingHoursOutput[]
	sun: TimeSlotWorkingHoursOutput[]
}

export type EditAvailabilityFormData = z.infer<typeof workingHoursSchema>

const AddBtn = ({ onClick }: { onClick: () => void }) => {
	const { t } = useLingui()
	return (
		<Button
			variant='ghost'
			size='sm'
			icon
			onClick={onClick}
			aria-label={t`Ajouter un créneau`}
		>
			<PlusIcon />
		</Button>
	)
}

const DeleteBtn = ({ onClick }: { onClick: () => void }) => {
	const { t } = useLingui()
	return (
		<Button
			variant='ghost'
			size='sm'
			icon
			onClick={onClick}
			aria-label={t`Supprimer le créneau`}
		>
			<TrashIcon />
		</Button>
	)
}

const TimeslotEditor = ({
	fieldName,
	actionMode,
	onAdd,
	onDelete,
}: {
	fieldName: string
	actionMode: 'delete' | 'add'
	onAdd: VoidFunction
	onDelete: VoidFunction
}) => {
	const form = useFormContext<EditAvailabilityFormData>()

	const validateTimeSlot = async (fieldPath: string) => {
		const slotValues = form.getValues(
			fieldPath as FieldPath<EditAvailabilityFormData>,
		) as {
			startTime?: string
			endTime?: string
		}

		if (slotValues?.startTime && slotValues?.endTime) {
			await form.trigger(
				`${fieldPath}.startTime` as FieldPath<EditAvailabilityFormData>,
			)
			await form.trigger(
				`${fieldPath}.endTime` as FieldPath<EditAvailabilityFormData>,
			)
		}
	}

	const startTimeError =
		form.formState.errors[fieldName.split('.')[0] as DayKey]?.[
			Number.parseInt(fieldName.split('.')[1], 10)
		]?.startTime

	return (
		<div className='flex flex-col gap-1'>
			<div className='flex items-center gap-2'>
				<Controller
					name={`${fieldName}.startTime` as FieldPath<EditAvailabilityFormData>}
					control={form.control}
					render={({ field }) => (
						<TimePicker
							value={field.value as string}
							onChange={async (event) => {
								field.onChange(event.target.value)
								validateTimeSlot(fieldName)
							}}
						/>
					)}
				/>

				<span className='text-muted'>-</span>

				<Controller
					name={`${fieldName}.endTime` as FieldPath<EditAvailabilityFormData>}
					control={form.control}
					render={({ field }) => (
						<TimePicker
							value={field.value as string}
							onChange={async (event) => {
								field.onChange(event.target.value)
								validateTimeSlot(fieldName)
							}}
						/>
					)}
				/>

				{actionMode === 'add' && <AddBtn onClick={onAdd} />}
				{actionMode === 'delete' && <DeleteBtn onClick={onDelete} />}
			</div>

			{startTimeError && (
				<p className='text-destructive text-xs font-medium'>
					{startTimeError.message}
				</p>
			)}
		</div>
	)
}

const DayEditor = ({ day, label }: { day: DayKey; label: string }) => {
	const { t } = useLingui()

	const form = useFormContext<EditAvailabilityFormData>()

	const { fields, remove, append } = useFieldArray({
		control: form.control,
		name: day,
	})

	const add = () => append({ startTime: '09:00', endTime: '17:00' })

	return (
		<div className='grid gap-y-1 px-4 py-2'>
			<div className='flex items-start'>
				<Label className='flex h-[38px] w-[100px] items-center gap-2'>
					<Checkbox
						checked={!!fields.length}
						onCheckedChange={() => {
							if (fields.length) remove()
							else add()
						}}
					/>

					<span className='text-sm font-semibold'>{label}</span>
				</Label>

				<div className='flex flex-1 flex-col gap-2'>
					{!fields.length && (
						<div className='text-muted-foreground flex-1 pt-1.5 text-center text-sm'>
							{t`Indisponible`}
						</div>
					)}

					{!!fields.length &&
						fields.map((field, index) => (
							<TimeslotEditor
								key={field.id}
								fieldName={`${day}.${index}`}
								onAdd={add}
								onDelete={() => remove(index)}
								actionMode={index === 0 ? 'add' : 'delete'}
							/>
						))}
				</div>
			</div>
		</div>
	)
}

export const EditAvailabilitiesForm = ({
	availabilities,
	onClose,
	onSubmit,
}: {
	availabilities: WorkingHoursOutput
	onClose?: VoidFunction
	onSubmit: (data: EditAvailabilityFormData) => void
}) => {
	const { t } = useLingui()

	const form = useForm<EditAvailabilityFormData>({
		resolver: zodResolver(workingHoursSchema),
		defaultValues: availabilities,
	})

	return (
		<Form {...form}>
			<form className='grid'>
				{Object.entries(DAYS_OF_WEEK).map(([key, value]) => (
					<DayEditor
						key={key}
						day={key as DayKey}
						label={value}
					/>
				))}

				<div className='flex justify-end gap-x-4'>
					<Button
						size='sm'
						variant='link'
						onClick={onClose}
					>
						{t`Annuler`}
					</Button>

					<Button
						size='sm'
						onClick={form.handleSubmit(onSubmit)}
					>
						{t`Valider`}
					</Button>
				</div>
			</form>
		</Form>
	)
}
